import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {Redirect, Route, Switch} from "react-router";
import {fromPairs, uniq} from "lodash";
import {BarebonesAppPage} from "../BarebonesAppPage";
import {recordEvent} from "../plugins/metricPlugin";

/**
 * This component takes routes and a site map in order to build the site navigation state.
 *
 * It routes the subcomponent to render, and provides transient navigation context.
 * It does not render content, and tries to stay away from any opinions about how things should be rendered,
 * just the semantic intention.
 *
 * @constructor
 */
export default function AppNavigator({
  siteMapTemplate = {
    routes: []
  },
  secondarySiteMaps = [],
  routes = [],
  defaultUrl,
  Layout = BarebonesAppPage,
  LayoutContextProvider = React.Fragment
}) {
  const [isDrillDown, setIsDrillDown] = useState(false);
  const [drilldownMenu, setDrillDownMenu] = useState([]);
  const [filterElement, setFilterElement] = useState(undefined);
  //Persists primary sitemap location in alternate sitemaps for the sitemap back button
  const [previousPrimarySitemapLocation, setPreviousPrimarySitemapLocation] = useState("/#/");

  // active child node history. sneaky mutability here
  // SORRY! performance cludge. Mutability. No need to trigger a re-render in these cases,
  // and need to signal across components
  const {getPreviouslyActiveChildPath, noteChildActive} = useMemo(() => {
    const activeChildNodeHistory = {};
    return {
      getPreviouslyActiveChildPath(parent) {
        const activePath = activeChildNodeHistory[parent.path] || parent.children[0].path;
        return activePath;
      }, 
      noteChildActive(childNode) {
        const parent = childNode.parentPath && flatRoutes.find(p => p.path === childNode.parentPath);
        if (parent && !childNode.excludeFromMenu) {
          activeChildNodeHistory[parent.path] = childNode.path;
        }

      }
    }
  }, []);

  const setDrillDown = (isDrillDown, menu) => {
    setIsDrillDown(isDrillDown);
    setDrillDownMenu(menu);
  };

  const routeIndex = fromPairs(
    routes
      .flatMap(x => {
        function liftDrillInPages(node) {
          if (!!node) {
            const drillInPages = (node.drillInPages || [])
              .map(drillInPage => ({ ...drillInPage, parent: node.path, parentTitle: node.title }))
              .flatMap(liftDrillInPages);
            return [node].concat(drillInPages);
          }
          return [];
        }
        return liftDrillInPages(x);
      })
      .map(x => [x.path, x])
  );

  // Given a Site Map and an index of routes, traverses the site map and "inflates" it with navigation data from the route index.
  function inflateNodesWithRouteData(parents, nodes, mapId) {
    return nodes.flatMap(node => {
      const isParent = !!node.children && !!node.children.length;
      if (!isParent) {
        const inflatedNode = inflateNodeWithRouteData(node, parents, routeIndex, mapId);
        return inflatedNode ? inflatedNode : [];
      } else {
        const children = inflateNodesWithRouteData(parents.concat([node.path]), node.children, mapId);
        if (!children.length) {
          return []
        } else {
          return [{
            ...node,
            siteMapId: mapId,
            active: false,
            isParent: true,
            parents: parents,
            children: children
          }]
        }
      }
    })
  }

  // Takes a node, its parent nodes, and a routeIndex and returns a node with all the navigation data filled in.
  const inflateNodeWithRouteData = (node, parentNodes, routeIndex, mapId) => {
    if (!routeIndex) return undefined;
    const route = routeIndex[node.path];
    // Guard against disabled routes
    if (!route || route.notPermitted) return undefined;
    return {
      ...node,
      active: false,
      drillInPages: route.drillInPages,
      isParent: false,
      component: route.component,
      parents: parentNodes,
      eventName: route.eventName,
      siteMapId: mapId,
      exact: route.exact
    };
  };


  // Create the linked map with sitemap key so we can identify the active map on route render.
  const linkedDefaultSiteMap = {
    ...siteMapTemplate,
    routes: inflateNodesWithRouteData([], siteMapTemplate.routes, 0)
  };
  const linkedAlternateSiteMaps = secondarySiteMaps.map((x, index) => ({
    ...x,
    routes: inflateNodesWithRouteData([], x.routes, index + 1)
  }));
  const allLinkedSiteMaps = [linkedDefaultSiteMap, ...linkedAlternateSiteMaps];
  const inflatedNodes = linkedDefaultSiteMap.routes.concat(...linkedAlternateSiteMaps.map(x => x.routes));

  function flatten(nodes, parent) {
    return nodes.flatMap(route => {
      const thisNode = [{...route, parentPath: parent && parent.path }];
      if (!route.children) {
        return thisNode;
      } else {
        return flatten(route.children, route).concat(thisNode);
      }
    });
  }

  // put prefix routes, such as "/parent" last so that child paths, such as "/parent/child"
  // have higher precedence.
  const flatRoutes = flatten(inflatedNodes).sort((a, b) => {
    return b.path.localeCompare(a.path);
  });
  // allow pass through of additional routes not specified in site map
  const allRouteKeys = Object.keys(routeIndex);
  const navRoutesSet = new Set(flatRoutes.map(x => x.path));
  const additionalRoutes = allRouteKeys.filter(x => !navRoutesSet.has(x)).map(x => routeIndex[x]);
  const redirectToUrl = !!defaultUrl ? defaultUrl : inflatedNodes[0].path;

  const sharedContext = {
    drilldown: isDrillDown,
    drilldownMenu: drilldownMenu,
    setDrillDown: setDrillDown,
    filterElement,
    setFilterElement,
    setPreviousPrimarySitemapLocation,
    previousPrimarySitemapLocation
  }

  const tagActive = (x) => {
    return (node) => {
      if (!node.isParent) {
        const drillInPagesActive = (node.drillInPages || []).map(tagActive(x));
        const isActive = node.path === x.path || drillInPagesActive.some(x => x.active);
        return {
          ...node,
          active: isActive
        }
      } else {
        const ownedPages = (node.children || []).concat(node.drillInPages || []);
        const childrenActive = ownedPages.map(tagActive(x));
        const isActive = childrenActive.some(x => x.active);
        return {
          ...node,
          active: isActive,
          children: childrenActive
        }
      }
    }
  };

  // Used in L2 navigation. Makes all nodes part of a section, using a 'sectionTag' string to do so.
  // If a sectionTag is not present on a node, it adds them to a "Default" section.
  function partitionNodesIntoSections(nodes) {
    if (!nodes) return [];
    const uniqueTags = uniq(nodes.map(n => n.sectionTag ? n.sectionTag : "Default"));
    const sections = uniqueTags.map(tag => {
      if (tag === "Default") {
        return {title: tag, hideTitle: false, isSection: true, children: nodes.filter(n => !n.sectionTag || (n.sectionTag && n.sectionTag === "Default"))};
      } else {
        return {title: tag, hideTitle: false, isSection: true, children: nodes.filter(n => n.sectionTag && n.sectionTag === tag)};
      }
    });

    // Return the Default Section on top, if present.
    const defaultSection = sections.filter(s => s.title === "Default");
    const otherSections = sections.filter(s => s.title !== "Default");
    return [...defaultSection, ...otherSections];
  }

  return <LayoutContextProvider><Switch>
    {
      additionalRoutes.map(x => {
       return <Route
          key={x.path}
          path={x.path}
          exact={x.exact}
          render={(routeProps) => {
            const Component = x.component;
            // Get the active routes and then flatten them
            const activeRoutes = allLinkedSiteMaps[0].routes.map(tagActive(x));

            const navL1HasSections = activeRoutes.some(x => x.sectionTag);
            const navL1 = navL1HasSections ? partitionNodesIntoSections(activeRoutes) : activeRoutes;

            return <AppNavigatorContext.Provider
              value={{
                siteMap: activeRoutes,
                navL1: navL1,
                navL2: [],
                navL3: [],
                activeNode: x, // TODO: Grab the parent route and find the root.
                ...sharedContext
              }}
            ><Layout><Component {...routeProps}/></Layout></AppNavigatorContext.Provider>
          }}/>})
    }
    {
      flatRoutes.filter(x => !x.isParent || (!routeIndex[x.path])).map(x => {
        if (x.isParent) {
          return <Route key={x.path} path={x.path} exact render={() => {
            noteChildActive(x);
            return <Redirect to={getPreviouslyActiveChildPath(x)}/>
          }
          } />
        } else {
          const route = routeIndex[x.path];
          const Component = route.component;
          return <Route key={x.path} path={x.path} exact={route.exact} render={(routeProps) => {

            noteChildActive(x);
            // Get the active routes and then flatten them
            // Find the active sitemap based off the active node
            const activeMap = allLinkedSiteMaps[x.siteMapId] || allLinkedSiteMaps[0];
            const activeRoutes = activeMap.routes.map(tagActive(x));

            const navL1HasSections = activeRoutes.some(x => x.sectionTag);
            const navL1 = navL1HasSections ? partitionNodesIntoSections(activeRoutes) : activeRoutes;
            const active1 = navL1.find(x => x.active) || [];
            const sectionedNavL2 = partitionNodesIntoSections(active1.children);
            const navL2Children = sectionedNavL2.flatMap(x => x.children);
            const active2 = navL2Children.find(x => x.active);
            const navL3 = active2 && active2.children || [];
            return <AppNavigatorContext.Provider
              value={{
                siteMap: activeRoutes,
                navL1: navL1.filter(x => !x.excludeFromMenu),
                navL2: navL2Children.some(x => !x.excludeFromMenu) ? sectionedNavL2 : [],
                navL3: navL3.filter(x => !x.excludeFromMenu),
                activeNode: x,
                hideNav: activeMap.hideNav,
                isDrillInSiteMap: activeMap.drillIn,
                siteMapId: x.siteMapId,
                siteMapTitle: activeMap.siteMapTitle,
                location: routeProps.location,
                ...sharedContext,
              }}
            >
              <Layout>
                <TagPageEvent eventName={route.eventName}>
                  <Component {...routeProps} />
                </TagPageEvent>
              </Layout>
            </AppNavigatorContext.Provider>
          }}/>
        }
      })
    }
    {
      !redirectToUrl
        ? <Route path="/" render={() => <Layout><div>No Routes Configured</div></Layout>} />
        : <Route path="/" render={() => <Redirect to={redirectToUrl} /> } />
    }
  </Switch>
  </LayoutContextProvider>

}

/**
 * @typedef {object} SiteMapNode
 * @property {string} path - path that this node links to. If no children are defined on this node, needs to have a
 *                           corresponding route with the exact same path in order to be rendered, otherwise assumes
 *                           that the route is blocked for access control
 * @property {string} title - title to be displayed in menu
 * @property {string} [icon] - icon class name to use in menu
 * @property {boolean} [excludeFromMenu] - true if this item should not be displayed in a corresponding level one
 *                                         through three menu, (for example "My Account")
 * @property {boolean} [isConfigPortal] - true if this item should "portal" to another route rather than navigating the
 *                                        browser
 * @property {boolean} [exact] - controls highlight state of the menu item. May be required if there's an index style
 *                               route path that is a prefix with other routes' paths
 * @property {SiteMapNode[]} [children] - array of child menu nodes. Only three levels of menu nodes are supported
 * @property {string} defaultUrl - the default route to redirect to should the user be at the root of the navigation
 *                                 if no url is specified, then the first available route will be used
 * @property {string} sectionTag - Used in L2 navigation to partition links into sections. The Section Tag is the label for the section.
 *                                 All nodes with a same sectionTag are grouped into the same section. Nodes without a sectionTag
 *                                 are assigned into a "Default" section. If a node has a single section, it is not displayed in the UI.
 */

const siteMapNode = PropTypes.shape({
  path: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  excludeFromMenu: PropTypes.bool, // do not display as a menu item, for routes that need more custom routing, e.g. my account
  isConfigPortal: PropTypes.bool,
  children: PropTypes.arrayOf(PropTypes.any), // children are recursive siteMapNode's
  defaultUrl: PropTypes.string,
  sectionTag: PropTypes.string
});

/**
 * Site Map Section
 * A SiteMapSection is a collection of SiteMapNode objects. Used to section off various items in the L2 navigation.
 * @typedef {object} SiteMapSection
 * @property {string} title - The name of the section.
 * @property {boolean} isSection - Convenient param to not have to evaluate the shape in JS-land.
 * @property {SiteMapNode[]} children - An array of SiteMapNode objects that belong to this SiteMapSection.
 */
const siteMapSection = PropTypes.shape({
  title: PropTypes.string,
  isSection: PropTypes.bool,
  children: PropTypes.arrayOf(siteMapNode) // children are recursive siteMapNode's
});

/**
 * @typedef {object} RouteNode
 * @property {string} path - "/some/path"
 * @property {object} component - a react component constructor / function
 * @property {boolean} [exact] - if the route should only match an exact path, not a prefix
 * @property {boolean} [notPermitted] - route should be excluded due to lacking permissions
 * @property {string} [eventName] - tracking event name to record when route is clicked
 */
const route = PropTypes.shape({
  path: PropTypes.string.isRequired,
  component: PropTypes.elementType.isRequired,
  exact: PropTypes.bool,
  notPermitted: PropTypes.bool
});

AppNavigator.propTypes = {
  /** defines the tree structure of the overall navigation */
  siteMapTemplate: PropTypes.shape({
    routes: PropTypes.arrayOf(siteMapNode)
  }),
  /** alternate site maps to support modifying the navigation on certain routes */
  secondarySiteMaps: PropTypes.arrayOf(PropTypes.shape({
    routes: PropTypes.arrayOf(siteMapNode)
  })),
  /** define all possible routes that site maps can navigate to. If a route is excluded here,
   * but listed in one of the site maps, it will be considered "in-accessible" due to user permission restrictions,
   * and removed from the navigation
   */
  routes: PropTypes.arrayOf(route),
  defaultUrl: PropTypes.string,
  /** a layout to wrap all child pages in */
  Layout: PropTypes.any,
  /** If the layout needs to retain state across pages, set the context provider with this prop */
  LayoutContextProvider: PropTypes.any
}



/**
 * Shared navigation and navigation menu state that can be consumed by an AppPage implementation
 */
export const AppNavigatorContext = React.createContext({
  /** entire site map, including routes hidden from the primary navigation */
  siteMap: [],
  /** nav items at each supported depth. Filtered to those that should be displayed in a nav list */
  navL1: [],
  navL2: [],
  navL3: [],
  /** route config for the currently active route (includes stuff like page title and icon) */
  activeNode: undefined,
  /** When true, AppPage should exhibit some kind of back behavior (that turns drilldown mode off).
   * Leaves rendering and sub-page up to the currently active component */
  drilldown: false,
  /** a drilldown menu element that should be rendered in place of the current L2 menu */
  drilldownMenu: undefined,
  /** set drilldown mode on or off. AppPage should exhibit some kind of back behavior.
   *  Additionally can set menuComponent
   */
  setDrilldown: (boolean, menuComponent) => {},
  /** Passed to be able to identify which sitemap we are using in the layout. */
  siteMapId: undefined,
  /** Used to set the last primary sitemap location */
  location: undefined
});

/**
 * Records an event `eventName` when this component is rendered, or if the underlying eventName changes
 */
function TagPageEvent({ eventName, children }) {
  useEffect(() => {
    if (eventName) recordEvent(eventName);
  }, [eventName]);
  return children;
}

TagPageEvent.propTypes = {
  /** name of the event */
  eventName: PropTypes.string,
  children: PropTypes.any
}
